import React, { useState, useEffect, useCallback } from 'react';
import { Device, Enterprise, Store, DeviceTelemetryData, DeviceFilters, SortOptions } from '../../types/deviceTelemetry';
import { deviceTelemetryService } from '../../services/deviceTelemetryService';
import DeviceList from './DeviceList';
import DeviceDetails from './DeviceDetails';
import AlertsPanel from './AlertsPanel';
import './DeviceHealthDashboard.css';

interface DeviceHealthDashboardProps {
  user: any;
}

const DeviceHealthDashboard: React.FC<DeviceHealthDashboardProps> = ({ user }) => {
  const [telemetryData, setTelemetryData] = useState<DeviceTelemetryData | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<DeviceFilters>({});
  const [sortOptions, setSortOptions] = useState<SortOptions>({
    field: 'healthScore',
    direction: 'desc'
  });
  const [viewMode, setViewMode] = useState<'overview' | 'details' | 'alerts'>('overview');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds

  // Load telemetry data
  const loadTelemetryData = useCallback(async () => {
    try {
      setError(null);
      const response = await deviceTelemetryService.getDeviceTelemetryData();
      
      if (response.success) {
        setTelemetryData(response.data);
      } else {
        throw new Error(response.error || 'Failed to load telemetry data');
      }
    } catch (err) {
      console.error('Error loading telemetry data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load device data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh setup
  useEffect(() => {
    loadTelemetryData();

    let intervalId: NodeJS.Timeout | null = null;
    if (autoRefresh) {
      intervalId = setInterval(loadTelemetryData, refreshInterval);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [loadTelemetryData, autoRefresh, refreshInterval]);

  // Handle device selection
  const handleDeviceSelect = (device: Device) => {
    setSelectedDevice(device);
    setViewMode('details');
  };

  // Handle filters change
  const handleFiltersChange = (newFilters: DeviceFilters) => {
    setFilters(newFilters);
  };

  // Handle sort change
  const handleSortChange = (newSort: SortOptions) => {
    setSortOptions(newSort);
  };

  // Get filtered and sorted devices
  const getFilteredDevices = (): Device[] => {
    if (!telemetryData) return [];

    let allDevices: Device[] = [];
    telemetryData.enterprises.forEach(enterprise => {
      enterprise.stores.forEach(store => {
        allDevices.push(...store.devices);
      });
    });

    // Apply filters
    let filteredDevices = allDevices.filter(device => {
      if (filters.enterpriseId && device.enterpriseId !== filters.enterpriseId) return false;
      if (filters.storeId && device.storeId !== filters.storeId) return false;
      if (filters.deviceType && device.deviceType !== filters.deviceType) return false;
      if (filters.status && device.status !== filters.status) return false;
      if (filters.healthScoreMin && device.healthScore < filters.healthScoreMin) return false;
      if (filters.healthScoreMax && device.healthScore > filters.healthScoreMax) return false;
      if (filters.alertLevel) {
        const hasAlert = device.alerts.some(alert => alert.type === filters.alertLevel);
        if (!hasAlert) return false;
      }
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const searchableText = `${device.deviceName} ${device.storeName} ${device.enterpriseName} ${device.deviceId}`.toLowerCase();
        if (!searchableText.includes(searchLower)) return false;
      }
      
      return true;
    });

    // Apply sorting
    filteredDevices.sort((a, b) => {
      let aVal: any, bVal: any;
      
      switch (sortOptions.field) {
        case 'deviceName':
          aVal = a.deviceName;
          bVal = b.deviceName;
          break;
        case 'healthScore':
          aVal = a.healthScore;
          bVal = b.healthScore;
          break;
        case 'lastSeen':
          aVal = new Date(a.lastSeen).getTime();
          bVal = new Date(b.lastSeen).getTime();
          break;
        case 'status':
          aVal = a.status;
          bVal = b.status;
          break;
        case 'storeName':
          aVal = a.storeName;
          bVal = b.storeName;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortOptions.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOptions.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filteredDevices;
  };

  // Get health score color
  const getHealthScoreColor = (score: number): string => {
    if (score >= 90) return '#22c55e'; // green
    if (score >= 70) return '#eab308'; // yellow
    if (score >= 50) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  // Get status color
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'online': return '#22c55e';
      case 'offline': return '#6b7280';
      case 'error': return '#ef4444';
      case 'maintenance': return '#3b82f6';
      default: return '#6b7280';
    }
  };

  if (loading && !telemetryData) {
    return (
      <div className="device-health-dashboard loading">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading device health data...</p>
        </div>
      </div>
    );
  }

  if (error && !telemetryData) {
    return (
      <div className="device-health-dashboard error">
        <div className="error-message">
          <h3>Error Loading Device Data</h3>
          <p>{error}</p>
          <button onClick={loadTelemetryData} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const filteredDevices = getFilteredDevices();
  const summary = telemetryData?.summary;

  return (
    <div className="device-health-dashboard">
      {/* Header with controls */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>Device Health Monitoring</h1>
          <div className="last-updated">
            Last updated: {telemetryData ? new Date(telemetryData.lastUpdated).toLocaleString() : 'Never'}
          </div>
        </div>
        
        <div className="header-controls">
          <div className="refresh-controls">
            <label>
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
              />
              Auto-refresh
            </label>
            <select
              value={refreshInterval}
              onChange={(e) => setRefreshInterval(Number(e.target.value))}
              disabled={!autoRefresh}
            >
              <option value={10000}>10s</option>
              <option value={30000}>30s</option>
              <option value={60000}>1m</option>
              <option value={300000}>5m</option>
            </select>
          </div>
          
          <button onClick={loadTelemetryData} className="refresh-button" disabled={loading}>
            {loading ? 'Refreshing...' : 'Refresh Now'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="summary-cards">
          <div className="summary-card">
            <div className="card-content">
              <div className="card-value">{summary.totalDevices}</div>
              <div className="card-label">Total Devices</div>
            </div>
          </div>
          
          <div className="summary-card online">
            <div className="card-content">
              <div className="card-value">{summary.onlineDevices}</div>
              <div className="card-label">Online</div>
            </div>
          </div>
          
          <div className="summary-card offline">
            <div className="card-content">
              <div className="card-value">{summary.offlineDevices}</div>
              <div className="card-label">Offline</div>
            </div>
          </div>
          
          <div className="summary-card error">
            <div className="card-content">
              <div className="card-value">{summary.errorDevices}</div>
              <div className="card-label">Errors</div>
            </div>
          </div>
          
          <div className="summary-card health">
            <div className="card-content">
              <div className="card-value" style={{ color: getHealthScoreColor(summary.averageHealthScore) }}>
                {summary.averageHealthScore}%
              </div>
              <div className="card-label">Avg Health</div>
            </div>
          </div>
          
          <div className="summary-card alerts">
            <div className="card-content">
              <div className="card-value">{summary.criticalAlerts + summary.warningAlerts}</div>
              <div className="card-label">Active Alerts</div>
            </div>
          </div>
        </div>
      )}

      {/* View Mode Tabs */}
      <div className="view-tabs">
        <button
          className={`tab ${viewMode === 'overview' ? 'active' : ''}`}
          onClick={() => setViewMode('overview')}
        >
          Device Overview
        </button>
        <button
          className={`tab ${viewMode === 'details' ? 'active' : ''}`}
          onClick={() => setViewMode('details')}
          disabled={!selectedDevice}
        >
          Device Details {selectedDevice && `(${selectedDevice.deviceName})`}
        </button>
        <button
          className={`tab ${viewMode === 'alerts' ? 'active' : ''}`}
          onClick={() => setViewMode('alerts')}
        >
          Alerts ({summary?.criticalAlerts || 0} critical, {summary?.warningAlerts || 0} warnings)
        </button>
      </div>

      {/* Main Content */}
      <div className="dashboard-content">
        {viewMode === 'overview' && (
          <DeviceList
            devices={filteredDevices}
            enterprises={telemetryData?.enterprises || []}
            filters={filters}
            sortOptions={sortOptions}
            onDeviceSelect={handleDeviceSelect}
            onFiltersChange={handleFiltersChange}
            onSortChange={handleSortChange}
            loading={loading}
          />
        )}
        
        {viewMode === 'details' && selectedDevice && (
          <DeviceDetails
            device={selectedDevice}
            onBack={() => setViewMode('overview')}
          />
        )}
        
        {viewMode === 'alerts' && telemetryData && (
          <AlertsPanel
            devices={filteredDevices}
            onDeviceSelect={handleDeviceSelect}
          />
        )}
      </div>
    </div>
  );
};

export default DeviceHealthDashboard;